import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Mail, Lock, User, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { useAuth } from '../App';
import { api } from '../utils/api';

const Signup = () => {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [formData, setFormData] = useState({ name: '', username: '', email: '', password: '', referral_code: refCode });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
    }
  }, [refCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    setSubmitting(true);
    try {
      const user = await api.register(formData);
      loginUser(user);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center px-4 py-12" data-testid="signup-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
          <MonkeyLogo size={40} />
          <span className="text-2xl font-bold">LinkMonkey</span>
        </Link>

        <Card className="p-8 shadow-xl border-2">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Create Account</h1>
            <p className="text-muted-foreground">Start sharing your links today</p>
          </div>

          {refCode && (
            <div className="mb-6 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 flex items-center gap-2" data-testid="referral-notice">
              <Gift className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 dark:text-green-300">Referred by <strong>{refCode}</strong></p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="name" type="text" placeholder="John Doe" className="pl-10"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required data-testid="signup-name-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                <Input id="username" type="text" placeholder="username" className="pl-8"
                  value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s+/g, '') })}
                  required data-testid="signup-username-input" />
              </div>
              <p className="text-xs text-muted-foreground">Your profile will be: linkmonkey.io/{formData.username}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder="you@example.com" className="pl-10"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required data-testid="signup-email-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type="password" placeholder="********" className="pl-10"
                  value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required minLength={6} data-testid="signup-password-input" />
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="signup-submit-btn">
              {submitting ? 'Creating account...' : 'Create Account'}
            </Button>

            <p className="text-[11px] text-center text-muted-foreground leading-relaxed" data-testid="signup-legal-text">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="underline underline-offset-2 hover:text-foreground" data-testid="signup-terms-link">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground" data-testid="signup-privacy-link">Privacy Policy</Link>.
            </p>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">Sign in</Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">&larr; Back to home</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
